import React, { useState } from 'react';
import { useRouter } from 'next/router';
import { useRecoilValue } from 'recoil';
import { AnimatePresence } from 'framer-motion';
import { withTheme } from 'styled-components';
import Image from 'next/image';
import { ChevronRight } from '@styled-icons/boxicons-regular/ChevronRight';
import { CaretLeft } from '@styled-icons/boxicons-regular/CaretLeft';
import { CaretRight } from '@styled-icons/boxicons-regular/CaretRight';
import { pageFlow, positionStart, windowSize } from '@/recoil/atoms';
import { toKey, getPageAnimationVariants } from '@/helpers';
import Title from '@/components/typography/Title';
import SubTitle from '@/components/typography/SubTitle';
import Paragraph from '@/components/typography/Paragraph';
import Caption from '@/components/typography/Caption';
import CloseIcon from '@/components/icons/Close';
import data from './data';
import {
  SWorkSamples,
  SSlideshow,
  SNav,
  SSlideContent,
  SSlideHeader,
  SSlideMain,
  SSlideSpecs,
  SSlideTags,
  SDots,
  SDotItem,
  SDotButton,
} from './styles';

const WorkSamples = () => {
  const router = useRouter();
  const screenSize = useRecoilValue(windowSize);
  const boundingClientRect = useRecoilValue(positionStart);
  const flow = useRecoilValue(pageFlow);
  const [[currentIndex, direction], setCurrentIndex] = useState([0, 0]);
  const { origin } = flow;

  const handleClose = () => {
    router.push('/');
  };

  const paginate = ({ newDirection, newIndex }) => {
    setCurrentIndex((prevState) => {
      if (newIndex >= 0) {
        const dir = newIndex < prevState[0] ? -1 : 1;
        return [newIndex, dir];
      }
      return [currentIndex + newDirection, newDirection];
    });
  };

  const current = data[currentIndex];
  const { img, title, activity, link, desc, specs, tags } = current;

  const dots = [...Array(data.length)].map((_, i) => (
    <SDotItem key={toKey(data[i].title)}>
      <SDotButton
        data-testid={`dot-${toKey(data[i].title)}`}
        type="button"
        onClick={() => paginate({ newIndex: i })}
        isActive={i === currentIndex}
      >
        {data[i].title}
      </SDotButton>
    </SDotItem>
  ));

  const slideShowVariants = {
    enter: (direction) => {
      return {
        zIndex: 1,
        x: direction > 0 ? 1000 : -1000,
        opacity: 0,
      };
    },
    middle: {
      zIndex: 1,
      x: 0,
      opacity: 1,
    },
    exit: (direction) => {
      return {
        zIndex: 0,
        x: direction < 0 ? 1000 : -1000,
        opacity: 0,
      };
    },
  };

  return (
    <SWorkSamples
      data-testid="route-work-samples"
      initial="start"
      animate="end"
      exit="exit"
      variants={getPageAnimationVariants(flow, screenSize, boundingClientRect)}
      transition={{ duration: 0.1 }}
    >
      <SSlideshow>
        <CloseIcon
          alt={`Back to ${origin ? origin : 'home'}`}
          handleClose={handleClose}
        />
        <AnimatePresence initial={false} custom={direction}>
          <SSlideContent
            data-testid={`slide-${toKey(title)}`}
            key={currentIndex}
            custom={direction}
            variants={slideShowVariants}
            initial="enter"
            animate="middle"
            exit="exit"
            transition={{
              x: { type: 'spring', stiffness: 300, damping: 200 },
              opacity: { duration: 0.2 },
            }}
          >
            <SSlideHeader>
              <Title data-testid="slide-title">{title}</Title>
              <SubTitle>{activity}</SubTitle>
              <Caption>
                <a href={link} title={`Go to ${title}`}>
                  {link}
                </a>
              </Caption>
            </SSlideHeader>
            <Image
              width={595}
              height={288}
              src={`/images/work/${img}`}
              alt={title}
            />
            <SSlideMain>
              <Paragraph>{desc}</Paragraph>
              <SSlideSpecs>
                {specs.map((spec) => (
                  <li key={toKey(spec)}>
                    <ChevronRight size={24} />
                    {spec}
                  </li>
                ))}
              </SSlideSpecs>
              <SSlideTags>
                {tags.map((tag) => (
                  <li key={toKey(tag)}>{tag}</li>
                ))}
              </SSlideTags>
            </SSlideMain>
          </SSlideContent>
        </AnimatePresence>
        {currentIndex > 0 && (
          <SNav
            data-testid="prev-trigger"
            prev
            type="button"
            onClick={() => paginate({ newDirection: -1 })}
          >
            <CaretLeft size={32} />
          </SNav>
        )}
        {currentIndex < data.length - 1 && (
          <SNav
            data-testid="next-trigger"
            next
            type="button"
            onClick={() => paginate({ newDirection: 1 })}
          >
            <CaretRight size={32} />
          </SNav>
        )}
        <SDots>{dots}</SDots>
      </SSlideshow>
    </SWorkSamples>
  );
};

export default withTheme(WorkSamples);
